import { FC } from 'react'
import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { ProjectService } from 'src/services/project.service'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { getFullName } from 'src/utils/string-formatter.util'

export const RecentImports: FC<{ projectId: number }> = ({ projectId }) => {
  const { t } = useTranslation()
  const { users, hasPermission } = useCurrentProject()
  const canViewImports = hasPermission(EPermissionGrantSubjects.PATIENT, EPermissionGrantActions.GET)

  const { data: recentImports = [] } = useQuery({
    queryKey: ['batch-imports', projectId],
    queryFn: () => ProjectService.getBatchImports(projectId),
    enabled: canViewImports,
  })

  if (!canViewImports || recentImports.length === 0) return null

  return (
    <section className="c-mt-8">
      <h2 className="c-text-lg">{t('pages.upload.recent-imports')}</h2>
      <ul className="c-list-none c-p-0 c-m-0">
        {recentImports.map((recentImport) => {
          const author = users.find((user) => user.id === recentImport.authorUserId)
          return (
            <li key={recentImport.batchImportToken} className="c-py-1">
              <LocalizedLink
                to={`/${projectId}/medical/upload?batch=${encodeURIComponent(recentImport.batchImportToken)}`}
                className="c-text-primary c-underline"
              >
                {t('pages.upload.recent-import-label', {
                  date: getFormattedDate(new Date(recentImport.createdAt)),
                  time: dayjs(recentImport.createdAt).format('HH:mm'),
                })}
              </LocalizedLink>
              <span className="c-text-sm c-text-grey-600">
                {' · '}
                {[
                  recentImport.filename,
                  t('pages.upload.recent-import-rows', { count: recentImport.dataCount }),
                  author && getFullName({ firstname: author.firstname, lastname: author.lastname }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
